import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Upload, BookOpen, Eye, EyeOff, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import MaterialsManager from "@/components/MaterialsManager";

const empty = {
  title: "", description: "", type: "video", duration_hours: 1, validity_months: 12,
  modules: [{ title: "Módulo 1", description: "", duration_minutes: 10, order: 1, attention_checks: [], checkpoints: [] }],
  evaluation: { questions: [], passing_score: 70, max_attempts: 3, randomize: true },
  platform: "", meeting_url: "", instructor: "", scheduled_at: "",
};

export default function AdminCourses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const fileInputs = useRef({});

  const refresh = async () => { const { data } = await api.get("/courses"); setItems(data); };
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...empty, ...c, evaluation: c.evaluation || empty.evaluation, modules: c.modules?.length ? c.modules : empty.modules }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/courses/${editing}`, form);
      else { const r = await api.post("/courses", form); setEditing(r.data.id); }
      toast.success("Curso salvo");
      setOpen(false); refresh();
    } catch (e) { toast.error("Erro ao salvar"); }
  };

  const togglePublish = async (c) => {
    await api.patch(`/courses/${c.id}`, { published: !c.published });
    refresh();
  };

  const remove = async (id) => { if (!window.confirm("Remover este curso?")) return; await api.delete(`/courses/${id}`); refresh(); };

  const uploadVideo = async (courseId, mIdx, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("module_index", String(mIdx));
    try {
      await api.post(`/courses/${courseId}/upload-video`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vídeo carregado");
      refresh();
    } catch (e) { toast.error("Falha no upload"); }
  };

  const [genBusy, setGenBusy] = useState({});
  const generateAIVideo = async (courseId, mIdx) => {
    const key = `${courseId}-${mIdx}`;
    if (!window.confirm("Gerar vídeo narrado com IA para este módulo? Pode levar 1-2 minutos.")) return;
    setGenBusy((b) => ({ ...b, [key]: true }));
    try {
      const { data } = await api.post(`/courses/${courseId}/modules/${mIdx}/generate-video`, { voice: "onyx" });
      const jobId = data.job_id;
      toast.info("Geração iniciada — isso pode levar 1-2 minutos.");
      // Poll job status
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/video-jobs/${jobId}`);
          if (job.status === "done") {
            toast.success(`Vídeo gerado (${job.result?.slides_count || "?"} slides, ${Math.round(job.result?.duration_seconds || 0)}s)`);
            setGenBusy((b) => ({ ...b, [key]: false }));
            refresh();
            return;
          }
          if (job.status === "failed") {
            toast.error("Falha na geração: " + (job.error || "erro desconhecido"));
            setGenBusy((b) => ({ ...b, [key]: false }));
            return;
          }
          setTimeout(poll, 5000);
        } catch (e) {
          setGenBusy((b) => ({ ...b, [key]: false }));
          toast.error("Erro ao consultar job");
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      setGenBusy((b) => ({ ...b, [key]: false }));
      toast.error("Falha ao iniciar: " + (e.response?.data?.detail || e.message));
    }
  };

  const setModule = (i, key, val) => {
    const m = [...form.modules]; m[i] = { ...m[i], [key]: val }; setForm({ ...form, modules: m });
  };
  const addModule = () => setForm({ ...form, modules: [...form.modules, { title: `Módulo ${form.modules.length + 1}`, description: "", duration_minutes: 10, order: form.modules.length + 1, attention_checks: [], checkpoints: [] }] });
  const removeModule = (i) => setForm({ ...form, modules: form.modules.filter((_, j) => j !== i) });

  const addQuestion = () => setForm({ ...form, evaluation: { ...form.evaluation, questions: [...form.evaluation.questions, { text: "", type: "multiple_choice", options: ["", ""], correct_index: 0 }] } });
  const setQ = (i, key, val) => { const qs = [...form.evaluation.questions]; qs[i] = { ...qs[i], [key]: val }; setForm({ ...form, evaluation: { ...form.evaluation, questions: qs } }); };
  const removeQ = (i) => setForm({ ...form, evaluation: { ...form.evaluation, questions: form.evaluation.questions.filter((_, j) => j !== i) } });

  return (
    <div className="space-y-8" data-testid="admin-courses-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Conteúdo</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Gerenciar cursos</h1>
          <p className="text-slate-600 mt-2 text-sm">Crie, edite e publique cursos em vídeo ou treinamentos ao vivo.</p>
        </div>
        <Button className="btn-navy" onClick={openNew} data-testid="course-new-btn"><Plus className="w-4 h-4 mr-2" />Novo curso</Button>
      </header>

      {items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-600"><BookOpen className="w-10 h-10 mx-auto text-slate-400 mb-3" />Nenhum curso ainda. Crie o primeiro!</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((c) => (
            <Card key={c.id}><CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Badge variant="outline" className="text-[10px]">{c.type === "online_live" ? "Ao vivo" : "Vídeo"}</Badge>
                  <div className="font-display font-bold text-lg mt-1">{c.title}</div>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.description}</p>
                  <div className="text-xs text-slate-500 mt-2">{(c.modules || []).length} módulo(s) · {c.duration_hours || 0}h</div>
                </div>
                {c.published ? <Badge className="bg-emerald-100 text-emerald-700 border-0">Publicado</Badge> : <Badge variant="secondary">Rascunho</Badge>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`course-edit-${c.id}`}>Editar</Button>
                <Button size="sm" variant="outline" onClick={() => togglePublish(c)} data-testid={`course-publish-${c.id}`}>
                  {c.published ? <><EyeOff className="w-4 h-4 mr-1" />Despublicar</> : <><Eye className="w-4 h-4 mr-1" />Publicar</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`course-del-${c.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
              </div>
              {(c.modules || []).map((m, mi) => (
                <div key={mi} className="mt-3 bg-slate-50 rounded-md px-3 py-2 text-sm space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{mi + 1}. {m.title}</div>
                      <div className="text-xs text-slate-500">
                        {m.video_path || m.video_url ? "Vídeo carregado" : "Sem vídeo"} · {(m.materials || []).length} material(is)
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                        className="hidden"
                        ref={(el) => fileInputs.current[`${c.id}-${mi}`] = el}
                        onChange={(e) => { if (e.target.files?.[0]) uploadVideo(c.id, mi, e.target.files[0]); }}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => generateAIVideo(c.id, mi)}
                        disabled={!!genBusy[`${c.id}-${mi}`]}
                        data-testid={`gen-video-${c.id}-${mi}`}
                      >
                        {genBusy[`${c.id}-${mi}`]
                          ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Gerando…</>
                          : <><Sparkles className="w-4 h-4 mr-1 text-[var(--unigr-red)]" />Gerar vídeo com IA</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => fileInputs.current[`${c.id}-${mi}`]?.click()} data-testid={`upload-video-${c.id}-${mi}`}>
                        <Upload className="w-4 h-4 mr-1" />Enviar vídeo
                      </Button>
                    </div>
                  </div>
                  <MaterialsManager
                    courseId={c.id}
                    moduleIndex={mi}
                    materials={m.materials || []}
                    onChanged={refresh}
                  />
                </div>
              ))}
            </CardContent></Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar curso" : "Novo curso"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title-input" /></div>
              <div><Label>Tipo</Label>
                <select className="w-full border border-input rounded-md px-3 py-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="video">Curso em vídeo</option>
                  <option value="online_live">Treinamento online ao vivo</option>
                </select>
              </div>
            </div>
            <div><Label>Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>Carga (horas)</Label><Input type="number" step="0.5" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: parseFloat(e.target.value) })} /></div>
              <div><Label>Validade (meses)</Label><Input type="number" value={form.validity_months || ""} onChange={(e) => setForm({ ...form, validity_months: parseInt(e.target.value) || null })} /></div>
              <div><Label>Nota mínima %</Label><Input type="number" value={form.evaluation.passing_score} onChange={(e) => setForm({ ...form, evaluation: { ...form.evaluation, passing_score: parseInt(e.target.value) || 70 } })} /></div>
            </div>
            {form.type === "online_live" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Plataforma</Label>
                  <select className="w-full border border-input rounded-md px-3 py-2 text-sm" value={form.platform || ""} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                    <option value="">—</option><option value="teams">Microsoft Teams</option><option value="meet">Google Meet</option><option value="zoom">Zoom</option><option value="custom">URL personalizada</option>
                  </select>
                </div>
                <div><Label>Link de acesso</Label><Input value={form.meeting_url || ""} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} /></div>
                <div><Label>Instrutor</Label><Input value={form.instructor || ""} onChange={(e) => setForm({ ...form, instructor: e.target.value })} /></div>
                <div><Label>Data e hora</Label><Input value={form.scheduled_at || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} placeholder="01/03/2026 19:00" /></div>
              </div>
            )}

            {form.type === "video" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label>Módulos</Label>
                  <Button size="sm" variant="outline" onClick={addModule}><Plus className="w-4 h-4 mr-1" />Adicionar</Button>
                </div>
                <div className="space-y-3 mt-2">
                  {form.modules.map((m, i) => (
                    <Card key={i}><CardContent className="p-4 space-y-2">
                      <div className="grid sm:grid-cols-2 gap-2">
                        <Input placeholder="Título do módulo" value={m.title} onChange={(e) => setModule(i, "title", e.target.value)} />
                        <Input type="number" placeholder="Duração (min)" value={m.duration_minutes} onChange={(e) => setModule(i, "duration_minutes", parseInt(e.target.value) || 0)} />
                      </div>
                      <Textarea placeholder="Descrição" value={m.description || ""} onChange={(e) => setModule(i, "description", e.target.value)} />
                      <div className="text-right"><Button size="sm" variant="ghost" onClick={() => removeModule(i)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></div>
                    </CardContent></Card>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <Label>Avaliação ({form.evaluation.questions.length} questões)</Label>
                <Button size="sm" variant="outline" onClick={addQuestion}><Plus className="w-4 h-4 mr-1" />Nova questão</Button>
              </div>
              <div className="space-y-3 mt-2">
                {form.evaluation.questions.map((q, i) => (
                  <Card key={i}><CardContent className="p-4 space-y-2">
                    <Input placeholder="Enunciado" value={q.text} onChange={(e) => setQ(i, "text", e.target.value)} />
                    <div className="space-y-1">
                      {q.options.map((o, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <input type="radio" checked={q.correct_index === oi} onChange={() => setQ(i, "correct_index", oi)} title="Marcar como correta" />
                          <Input value={o} onChange={(e) => { const opts = [...q.options]; opts[oi] = e.target.value; setQ(i, "options", opts); }} placeholder={`Alternativa ${oi + 1}`} />
                          <Button size="sm" variant="ghost" onClick={() => setQ(i, "options", q.options.filter((_, j) => j !== oi))}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" onClick={() => setQ(i, "options", [...q.options, ""])}>+ Alternativa</Button>
                    </div>
                    <div className="text-right"><Button size="sm" variant="ghost" onClick={() => removeQ(i)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></div>
                  </CardContent></Card>
                ))}
              </div>
            </div>

            <Button className="btn-navy w-full" onClick={save} data-testid="course-save-btn">Salvar curso</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
